import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import "./Sidebar.css";
import { isAuthorised } from '../../utils/common';
import { MAIN_SIDEBAR_MENU } from '../../config/sidebar';
import { useSelector } from 'react-redux';
import SeedsLogo from "../../assets/images/SeedsLogo.png";
import UserProfileDropdown from '../UserProfile/UserProfile';

/**
 * SidebarItem
 * Collapsed → icon only + hover tooltip
 * Expanded  → icon + label side by side
 */
const SidebarItem = ({ item, officer, expanded, onExpand, onCollapse, onHover, onLeave, isSmallScreen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showSubmenu, setShowSubmenu] = useState(false);
  const ref = useRef(null);

  // Close child popup on outside click
  useEffect(() => {
    if (!item.children) return;
    const handleOut = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShowSubmenu(false);
    };
    document.addEventListener('mousedown', handleOut);
    return () => document.removeEventListener('mousedown', handleOut);
  }, [item.children]);

  // Auto-open accordion when a child route is active
  useEffect(() => {
    if (item.children) {
      const active = item.children.some(
        c => location.pathname === c.route || location.pathname.startsWith(c.route + '/')
      );
      if (active) setShowSubmenu(true);
    }
  }, [location.pathname, item.children]);

  if (!isAuthorised(officer, MAIN_SIDEBAR_MENU?.[item.key])) return null;

  const Icon = item.icon;

  const isActive = item.route
    ? location.pathname === item.route || location.pathname.startsWith(item.route + '/')
    : item.children?.some(c =>
        location.pathname === c.route || location.pathname.startsWith(c.route + '/')
      );

  const handleClick = () => {
    onLeave();
    if (item.children) {
      if (!expanded) {
        onExpand();
      }
      setShowSubmenu(prev => !prev);
    } else {
      if (expanded) {
        onCollapse();
        setTimeout(() => {
          navigate(item.route);
        }, 280); // Wait 280ms for the sidebar CSS transition to complete smoothly
      } else {
        onExpand();
        setTimeout(() => {
          navigate(item.route);
        }, 280); // Wait 280ms for the sidebar CSS transition to complete smoothly
      }
    }
  };

  return (
    <div
      className="tooltip-container"
      onMouseEnter={(e) => !isSmallScreen && !expanded && onHover(e, item.tooltip)}
      onMouseLeave={onLeave}
      ref={item.children ? ref : null}
    >
      {item.children ? (
        <div className="sidebarTrancheWrapper">
          <div className={`sidebarTrancheButton ${isActive ? 'active' : ''}`} onClick={handleClick}>
            <span className="sidebar-icon-slot">
              <Icon {...item.iconProps} />
            </span>
            <span className="sidebar-label-slot">{item.tooltip}</span>
            {expanded && (
              <span className={`sidebar-chevron-icon ${showSubmenu ? 'open' : ''}`}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            )}
          </div>

          {/* Inline accordion — only when expanded */}
          {expanded && showSubmenu && (
            <div className="sidebarAccordionChildren">
              {item.children.map(child => {
                const childActive =
                  location.pathname === child.route ||
                  location.pathname.startsWith(child.route + '/');
                return (
                  <div
                    key={child.key}
                    className={`sidebarChildRow ${childActive ? 'active' : ''}`}
                    onClick={() => {
                      onLeave();
                      onCollapse();
                      setTimeout(() => {
                        navigate(child.route);
                      }, 280); // Wait 280ms for the sidebar CSS transition to complete smoothly
                    }}
                  >
                    <span className="sidebarChildDot" />
                    {child.label}
                  </div>
                );
              })}
            </div>
          )}

          {/* Floating popup — only when collapsed */}
          {!expanded && showSubmenu && (
            <div className="trancheMenuPopup">
              {item.children.map(child => (
                <div key={child.key} onClick={() => { onLeave(); navigate(child.route); }}>
                  {child.label}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <button
          className={`sidebarTabsButton ${isActive ? 'active' : ''}`}
          onClick={handleClick}
        >
          <span className="sidebar-icon-slot">
            <Icon {...item.iconProps} />
          </span>
          <span className="sidebar-label-slot">{item.tooltip}</span>
        </button>
      )}
    </div>
  );
};

/**
 * Sidebar
 *
 * Collapsed (68px) by default — shows icon only.
 * Desktop: expands on hover, or via double-click toggle.
 * Tablet/Mobile (<=1024px): hover is disabled (unreliable on touch);
 * a dedicated tap toggle button controls expand/collapse instead,
 * Logo top | Nav middle (scrollable) | User profile bottom.
 */
const Sidebar = () => {
  const { officerProfile } = useSelector(store => ({
    officerProfile: store.UserReducer.officerProfile
  }));

  const [expanded, setExpanded] = useState(() => localStorage.getItem('sidebar_expanded') === 'true');
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [dropdownActive, setDropdownActive] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 1024px)').matches : false
  );
  const sidebarRef = useRef(null);

  // expand/collapse (desktop) or tap-based (tablet/mobile)
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1024px)');
    const handleChange = (e) => {
      setIsSmallScreen(e.matches);
      // Collapse automatically when crossing into small-screen mode
      // so the overlay doesn't stay stuck open from a desktop hover state
      if (e.matches) {
        updateExpanded(false);
      }
    };
    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange);
    } else {
      // Safari fallback
      mql.addListener(handleChange);
    }
    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', handleChange);
      } else {
        mql.removeListener(handleChange);
      }
    };
  }, []);

  // Close sidebar on click outside
  useEffect(() => {
    const handleOut = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        updateExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleOut);
    return () => document.removeEventListener('mousedown', handleOut);
  }, []);

  const handleHover = (e, text) => {
    if (isSmallScreen) return; 
    const rect = e.currentTarget.getBoundingClientRect();
    setActiveTooltip({ text, top: rect.top + rect.height / 2 });
  };
  const handleLeave = () => setActiveTooltip(null);

  const visibleItems = Object.entries(MAIN_SIDEBAR_MENU)
    .filter(([key]) => isAuthorised(officerProfile, MAIN_SIDEBAR_MENU?.[key]))
    .map(([key, props]) => ({ key, ...props }));

  const updateExpanded = (val) => {
    setExpanded(val);
    localStorage.setItem('sidebar_expanded', String(val));
  };

  const handleDoubleClick = () => {
    if (isSmallScreen) return; // small screens use the explicit tap toggle instead
    updateExpanded(!expanded);
  };

  const handleMobileToggleClick = (e) => {
    e.stopPropagation();
    updateExpanded(!expanded);
  };

  if (visibleItems.length === 0) return null;

  return (
    <div
      ref={sidebarRef}
      className={`dashboardSidebar${expanded ? ' sidebar-expanded' : ''}${dropdownActive ? ' sidebar-dropdown-active' : ''}`}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={() => !isSmallScreen && updateExpanded(true)}
      onMouseLeave={() => !isSmallScreen && updateExpanded(false)}
    >

      {/* ── Top: Logo + Mobile/Tablet Toggle ── */}
      <div className="sidebar-logo-section">
        <img src={SeedsLogo} alt="Seeds" className="sidebar-logo-img" />
        <button
          type="button"
          className="sidebar-mobile-toggle"
          onClick={handleMobileToggleClick}
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-expanded={expanded}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </div>

      {/* ── Middle: Scrollable nav ── */}
      <div className="sidebarMenuScrollable">
        <div className="sidebarMenu">
          {visibleItems.map(item => (
            <SidebarItem
              key={item.key}
              item={item}
              officer={officerProfile}
              expanded={expanded}
              onExpand={() => updateExpanded(true)}
              onCollapse={() => updateExpanded(false)}
              onHover={handleHover}
              onLeave={handleLeave}
              isSmallScreen={isSmallScreen}
            />
          ))}
        </div>
      </div>

      {/* ── Floating tooltip (collapsed only) ── */}
      {!isSmallScreen && !expanded && activeTooltip && (
        <div className="sidebar-global-tooltip" style={{ top: `${activeTooltip.top}px` }}>
          {activeTooltip.text}
        </div>
      )}

      {/* ── Bottom: User Profile ── */}
      <div className="sidebar-profile-section">
        <UserProfileDropdown sidebarExpanded={expanded} onToggle={(isOpen) => setDropdownActive(isOpen)} />
      </div>
    </div>
  );
};

export default Sidebar;